// Define stub for GenerateContentResponse to avoid @google/genai dependency in Shared
export interface GenerateContentResponse {
    candidates: any[];
    promptFeedback?: any;
    usageMetadata?: any;
    text: () => string;
}

// Define stub for Part to avoid @google/genai dependency in Shared
export type Part = any;

export type AppPage =
    | 'home'
    | 'canvas'
    | 'my-styles'
    | 'styling-advisor'
    | 'profile'
    | 'legal'
    | 'about'
    | 'services'
    | 'portfolio'
    | 'booking'
    | 'blog'
    | 'blog-post'
    | 'author'
    | 'guest-denied'
    | 'style-report'
    | 'deployment-guide'
    | 'help'
    | 'press'
    | 'trends'
    | 'travel'
    | 'color-studio'
    | 'capsule-generator'
    | 'service-selection'
    | 'digital-box'
    | 'style-lab'
    | 'studio'
    | 'try-on';

export type Page = AppPage;

export enum AppTab {
    DASHBOARD = 'DASHBOARD',
    PHOTO_STYLER = 'PHOTO_STYLER',
    OUTFIT_FINDER = 'OUTFIT_FINDER',
    COLOR_ANALYSIS = 'COLOR_ANALYSIS',
    STYLING_ADVISOR = 'STYLING_ADVISOR',
    WARDROBE = 'WARDROBE',
    STYLE_PULSE = 'STYLE_PULSE',
    STYLE_BOX = 'STYLE_BOX',
    MY_STYLE = 'MY_STYLE',
    STYLE_SANDBOX = 'STYLE_SANDBOX',
}

export enum PhotoStyle {
    NONE = 'NONE',
    CLASSIC = 'CLASSIC',
    BOHEMIAN = 'BOHEMIAN',
    MINIMALIST = 'MINIMALIST',
    STREETWEAR = 'STREETWEAR',
    SPORTY = 'SPORTY',
    VINTAGE = 'VINTAGE',
    GOTHIC = 'GOTHIC',
    PREPPY = 'PREPPY',
    FORMAL = 'FORMAL',
    CASUAL = 'CASUAL',
    AVANT_GARDE = 'AVANT_GARDE',
    BACKGROUND_BLUR = 'BACKGROUND_BLUR',
}

export type Generation = 'alpha' | 'z' | 'millennial' | 'x' | 'boomer' | 'classic';

export interface WardrobeItem {
    id: string;
    nameKey: string;
    url: string;
    iconUrl?: string;
    type: 'garment' | 'accessory';
    category?: string;
    defaultColor?: string;
    colorFamily?: string[];
    requiredMembership?: 'basic' | 'premium' | 'advanced' | 'nextgen';
    material?: string;
    brand?: string;
    price?: string;
    currency?: string;
    // Mobile specific (synced)
    lastWorn?: number;
    usageCount?: number;
    isProfessionalPose?: boolean;
    defaultAdjustments?: ItemAdjustment;
    originalFile?: File;
    retailerUrl?: string;
}

export interface UserProfile {
    id?: string;
    email?: string;
    username?: string;
    displayName?: string;
    avatar?: string;
    passwordHash?: string;
    accountStatus?: 'registered' | 'validated' | 'active' | 'inactive' | 'deleted' | 'activated';
    profileEnabled?: boolean;
    registrationDate?: string;

    measurements?: {
        [key: string]: string | undefined;
        height?: string;
        weight?: string;
        chest?: string;
        waist?: string;
        hips?: string;
        shoeSize?: string;
    };

    hairColor?: string;
    eyeColor?: string;
    faceShape?: string;
    skinTone?: string;
    bodyType?: string;
    bodySize?: string; // UIX alias
    ageRange?: string;
    seasonalColor?: string;
    colorSeason?: string; // UIX alias
    undertone?: string;

    stylePersona?: string;
    fashionGoals?: string;
    styleIcons?: string;

    generation?: Generation;
    lifestyle?: {
        officeTime?: 'none' | 'low' | 'medium' | 'high';
        outdoorTime?: 'none' | 'low' | 'medium' | 'high';
        travelTime?: 'none' | 'low' | 'medium' | 'high';
    };

    preferredColors?: string[];
    dislikedStyles?: string;
    preferredRetailers?: string[];

    membershipLevel?: 'free' | 'basic' | 'premium' | 'advanced' | 'nextgen' | string | MembershipLevel;
    membershipExpiry?: string;
    role?: 'ADMIN' | 'USER';

    saveImageQuality?: 'low' | 'medium' | 'high';
    connectedAccounts?: { [key: string]: string };
    generationCount?: number;
    generationLimitResetDate?: string;
    favoritePoses?: string[];
    styleAgents?: any[];
    stylePreferences?: {
        likedTags: string[];
        dislikedTags: string[];
    };
    onboardingCompleted?: boolean;
    savedStyles?: SavedStyleItem[];
    activationToken?: string;
    activationTokenExpiry?: string;
    socialChannels?: string[];
    archetype?: UserArchetype;
    innerDevelopmentPhase?: InnerDevelopmentPhase;
    activeLifeSphere?: LifeSphere;
    purchasedFeatures?: {
        [featureId: string]: {
            expiry?: string; // ISO date
            amount?: number; // Usage count
            remainingAmount?: number;
        };
    };
    colorAnalysis?: ColorAnalysisResult;
}

export enum UserArchetype {
    MUSE = 'MUSE',
    WARRIOR = 'WARRIOR',
    CREATOR = 'CREATOR',
    HEALER = 'HEALER',
    RULER = 'RULER',
    MAGICIAN = 'MAGICIAN'
}

export enum InnerDevelopmentPhase {
    EMERGENCE = 'EMERGENCE',
    LEADERSHIP = 'LEADERSHIP',
    RECLAMATION = 'RECLAMATION',
    TRANSITION = 'TRANSITION',
    MASTERY = 'MASTERY'
}

export enum LifeSphere {
    EXECUTIVE_ASCENDANCE = 'EXECUTIVE_ASCENDANCE',
    ROMANTIC_RENAISSANCE = 'ROMANTIC_RENAISSANCE',
    EFFORTLESS_MATRIARCH = 'EFFORTLESS_MATRIARCH',
    CREATIVE_FLOURISHING = 'CREATIVE_FLOURISHING'
}

export type PaymentMethod = 'card' | 'paypal' | 'apple_pay' | 'google_pay' | 'twint' | 'sofort' | 'klarna';

export interface PaymentDetails {
    cardName?: string;
    cardNumber?: string;
    expiryDate?: string;
    cvv?: string;
    email?: string;
    phone?: string;
    method?: PaymentMethod;
}

export interface VoucherDetails {
    recipientEmail: string;
    recipientName?: string;
    senderEmail?: string;
    senderName?: string;
    message?: string;
    amount?: string;
    serviceIds?: string[];
    paymentDetails?: PaymentDetails;
}

export enum MembershipLevel {
    FREE = 'FREE',
    BASIC = 'BASIC',
    PREMIUM = 'PREMIUM',
    PROFESSIONAL = 'PROFESSIONAL',
    ADVANCED = 'ADVANCED',
    NEXTGEN = 'NEXTGEN',
}

export interface MembershipTier {
    id: string;
    nameKey: string;
    featuresKey: string[];
    isFeatured?: boolean;
    prices: { cycle: string; priceKey: string; basePriceEUR: number; discountKey?: string }[];
}

export interface ContactDetails {
    name: string;
    email: string;
    phone?: string;
    subject?: string;
    message: string;
}

export interface BookingDetails {
    serviceIds: string[];
    date: string;
    time: string;
    name: string;
    email: string;
    phone?: string;
    notes?: string;
}

export interface RssFeedItem {
    id: string;
    title: string;
    source: string;
    sourceKey: string;
    link: string;
    pubDate: string;
    snippet: string;
    snippetKey?: string;
    imageUrl?: string;
    titleKey?: string;
}

export interface NewArrivalItem {
    id: string;
    nameKey: string;
    price: string;
    imageUrl: string;
    descriptionKey: string;
}

/**
 * Chat and Advice Types
 */
export interface ChatMessage {
    id?: string;
    role: 'user' | 'model';
    text?: string;
    parts?: Part[];
    timestamp?: number;
    isTyping?: boolean;
    feedback?: 'like' | 'dislike' | { rating: 'up' | 'down'; tag?: any } | null;
    isError?: boolean;
    imageUrl?: string;
}

export enum UserRole {
    ADMIN = 'ADMIN',
    USER = 'USER'
}

export interface GeminiChatHistoryEntry {
    role: 'user' | 'model';
    parts: Part[];
}

/**
 * Outfit & Style Discovery Enums
 */
export enum OutfitFit {
    ANY = 'ANY',
    SLIM = 'SLIM',
    REGULAR = 'REGULAR',
    RELAXED = 'RELAXED',
    OVERSIZED = 'OVERSIZED'
}

export enum OutfitBudget {
    ANY = 'ANY',
    BUDGET = 'BUDGET',
    MID_RANGE = 'MID_RANGE',
    LUXURY = 'LUXURY'
}

export enum OutfitStyle {
    ANY = 'ANY',
    GLAMOUR = 'GLAMOUR',
    STREETWEAR = 'STREETWEAR',
    MINIMALIST = 'MINIMALIST',
    BOHEMIAN = 'BOHEMIAN',
    VINTAGE = 'VINTAGE',
    CASUAL = 'CASUAL',
    FORMAL = 'FORMAL',
    SPORTY = 'SPORTY',
    PREPPY = 'PREPPY',
    GOTHIC = 'GOTHIC',
    AVANT_GARDE = 'AVANT_GARDE'
}

export enum OutfitOccasion {
    ANY = 'ANY',
    CASUAL = 'CASUAL',
    FORMAL = 'FORMAL',
    BUSINESS = 'BUSINESS',
    PARTY = 'PARTY',
    SPORTY = 'SPORTY',
    BEACH = 'BEACH',
    EVERYDAY = 'EVERYDAY',
    COCKTAIL = 'COCKTAIL',
    WEDDING = 'WEDDING',
    TRAVEL = 'TRAVEL'
}

export enum OutfitSeason {
    ANY = 'ANY',
    SPRING = 'SPRING',
    SUMMER = 'SUMMER',
    AUTUMN = 'AUTUMN',
    WINTER = 'WINTER',
    ALL_SEASONS = 'ALL_SEASONS'
}

export enum OutfitColor {
    ANY = 'ANY',
    RED = 'RED',
    BLUE = 'BLUE',
    GREEN = 'GREEN',
    YELLOW = 'YELLOW',
    ORANGE = 'ORANGE',
    PURPLE = 'PURPLE',
    PINK = 'PINK',
    BLACK = 'BLACK',
    WHITE = 'WHITE',
    GRAY = 'GRAY',
    BROWN = 'BROWN',
    BEIGE = 'BEIGE',
    PASTEL = 'PASTEL',
    NEON = 'NEON',
    METALLIC = 'METALLIC',
    EARTH_TONES = 'EARTH_TONES'
}

export enum OutfitGender {
    ANY = 'ANY',
    WOMEN = 'WOMEN',
    MEN = 'MEN',
    UNISEX = 'UNISEX',
    KIDS = 'KIDS'
}

export interface GenerationalTrend {
    id: number;
    titleKey: string;
    brand: string;
    imageUrl: string;
    price: string;
    tagKey: string;
    generation: Generation;
}

export enum StylistPersona {
    STANDARD = 'STANDARD',
    VOGUE = 'VOGUE',
    STREETWEAR = 'STREETWEAR',
    CLASSIC = 'CLASSIC',
    BOHEMIAN = 'BOHEMIAN',
    AVANT_GARDE = 'AVANT_GARDE',
    MINIMALIST = 'MINIMALIST'
}

export type AgentRole = 'stylist' | 'shopper' | 'booking' | 'advisor' | 'researcher' | 'system';

export interface AgentAction {
    type: 'OPEN_COLOR_PICKER' | 'ADD_TO_BASKET' | 'BOOK_SESSION' | 'SHOW_PRODUCTS' | 'SAVE_CONTENT' | 'NAVIGATE' | 'SHOW_PLAN' | 'CONFIRMATION';
    payload: any;
}

export interface AgentResponse {
    role: AgentRole;
    text: string;
    actions?: AgentAction[];
    delegatedTo?: AgentRole;
    thoughtProcess?: string;
    statusSteps?: { message: string, status: 'complete' | 'in-progress' | 'error' }[];
    sessionId?: string;
}

export interface OrchestratorContext {
    userProfile: UserProfile;
    currentLayer: OutfitLayer;
    savedOutfits: SavedOutfit[];
}

export interface RetailItem {
    name: string;
    price: string;
    retailer: string;
    imageUrl: string;
    productUrl: string;
    description?: string;
    matchReason?: string;
}

export interface Author {
    id: string;
    nameKey: string;
    bioKey: string;
    avatarUrl: string;
}

export interface BlogPost {
    id: string;
    titleKey: string;
    excerptKey: string;
    imageUrl: string;
    date?: string;
    dateKey?: string;
    dateModified?: string;
    authorId?: string;
    contentKey?: string;
    /** True for posts fetched from an external RSS feed */
    isExternal?: boolean;
    /** External link URL for diary/RSS posts */
    externalUrl?: string;
    /** Raw title string (used instead of titleKey for external posts) */
    title?: string;
    /** Raw excerpt string (used instead of excerptKey for external posts) */
    excerpt?: string;
}

export interface Campaign {
    id: string;
    nameKey: string;
    descriptionKey: string;
    serviceIds: string[];
    imageUrl: string;
    badgeKey: string;
    discountPercentage?: number;
    discountAmount?: number;
    targetTierIds?: string[];
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
}

export interface Booklet {
    id: string;
    titleKey: string;
    descriptionKey: string;
    category: string;
    coverImageUrl: string;
    fileSize: string;
    downloadUrl?: string;
}

export interface PricingTier {
    id: string;
    nameKey: string;
    priceKey: string;
    basePriceEUR: number;
    isFeatured?: boolean;
    featuresKey: string[];
}

export interface PortfolioItem {
    id: string;
    titleKey: string;
    descriptionKey: string;
    imageUrl: string;
    categoryKey: string;
    isActive?: boolean;
    isOnlineService?: boolean;
    pricingTiers: PricingTier[];
}

export interface Pose {
    instruction: string;
    labelKey: string;
    previewUrl: string;
}

export interface PoseCategory {
    nameKey: string;
    poses: Pose[];
    requiredMembership?: 'basic' | 'premium' | 'advanced' | 'nextgen';
}

export interface SavedStyleItem {
    id: string;
    name: string;
    type: 'PRESET' | 'CUSTOM' | 'GENERAL';
    value: string;
    timestamp: number;
}

export interface SavedContent {
    id: string;
    type: 'tips' | 'plan' | 'color_analysis' | 'analysis' | 'social_media_post' | 'search';
    title: string;
    content: string;
    createdAt: string;
    sourceImageUrl?: string;
    linkedinText?: string;
    linkedinImageUrl?: string;
    twitterText?: string;
    twitterImageUrl?: string;
    instagramText?: string;
    instagramImageUrl?: string;
}

export interface Lookbook {
    id: string;
    name: string;
    type: 'collage' | 'shopping-video' | 'magazine';
    outfitIds: string[];
    createdAt: string;
    lookbookImageUrl?: string;
    videoUrl?: string;
    relatedRetailItems?: any[];
    editorialNarrative?: string;
}

export interface ColorAnalysisResult {
    season: string;
    characteristics: {
        skinTone: string;
        hairColor: string;
        eyeColor: string;
        contrast: string;
        faceShape?: string;
        bodySize?: string;
    };
    palette: {
        powerColors: string[];
        neutrals: string[];
        avoidColors: string[];
    };
    advice: {
        clothing: string;
        hair: string;
        makeup: string;
        jewelry: string;
    };
}

export interface ItemAdjustment {
    fit?: 'tight' | 'regular' | 'loose' | 'oversized';
    placement?: string;
    scale?: number;
    rotation?: number;
    xOffset?: number;
    yOffset?: number;
}

export interface SceneConfiguration {
    cameraView: 'close-up' | 'waist-up' | 'full-body';
    backgroundPrompt: string;
    modelPosition: { x: number; y: number };
    modelScale: number;
}

export interface OutfitLayer {
    garments: any[];
    mainGarment?: any; // UIX compatibility
    accessories: any[];
    appliedGarmentColors: { [garmentId: string]: string };
    appliedMainGarmentColor?: string;
    appliedAccessoryColors: { [accessoryId: string]: string };
    itemAdjustments: { [itemId: string]: any };
    poseOutputs: { [poseInstruction: string]: string };
    poseDescriptions?: { [poseId: string]: string };
    currentPoseInstruction: string;
    sceneConfig?: any;
    attitudeNote?: string;
    postureGuidance?: string;
}

export interface StyleAgentConfig {
    id: string;
    name: string;
    isActive: boolean;
    frequency: 'weekly' | 'monthly';
    targetSeason: string;
    focusArea: string;
    nextRun: string;
    retailers: string[];
    useSavedStyles: boolean;
}

export interface SavedOutfit {
    id: string;
    name: string;
    description?: string;
    tags?: string[];
    previewImageUrl: string | null;
    layer: OutfitLayer;
    baseModelImageUrl: string;
    attitudeNote?: string;
    postureGuidance?: string;
}

export interface StyleReport {
    summary: string;
    score: number;
    colorAnalysis: {
        matchesSeason: boolean;
        season: string;
        colorHarmony: string;
        recommendations: string[];
    };
    agentReviews: {
        agentName: string;
        rating: number;
        feedback: string;
    }[];
    fitSummary: {
        overallStatus: string;
        criticalAreas: string[];
    };
}

export interface GeminiGenerateContentResponse {
    response: GenerateContentResponse;
}

export interface AccessorySet {
    id: string;
    nameKey: string;
    accessoryIds: string[];
    garmentIds?: string[];
    previewImageUrl: string;
    descriptionKey?: string;
}

export interface CloudData {
    outfits: SavedOutfit[];
    lookbooks: Lookbook[];
    profile: any;
    savedContent: SavedContent[];
    favorites: string[];
}

export interface Testimonial {
    id: string;
    nameKey: string;
    locationKey: string;
    quoteKey: string;
    avatarUrl: string;
    serviceKey: string;
}

export interface InstagramReel {
    id: string;
    thumbnailUrl: string;
    videoUrl: string;
    author: string;
    avatarUrl: string;
    caption: string;
    captionKey?: string;
    timestamp?: string;
}

export interface YouTubeVideo {
    id: string;
    title?: string;
    titleKey?: string;
    thumbnailUrl: string;
    channel: string;
    views: string;
    publishedKey?: string;
    publishedAt?: string;
    videoId?: string;
}

export interface Tweet {
    id: string;
    author?: string;
    authorKey?: string;
    handle?: string;
    avatarUrl?: string;
    text?: string;
    textKey?: string;
    timestamp?: string;
    timestampKey?: string;
}

/**
 * Logging & Telemetry Types
 */
export enum LogLevel {
    DEBUG = 0,
    INFO = 1,
    WARN = 2,
    ERROR = 3
}

export interface LogEntry {
    timestamp: string;
    level: string;
    message: string;
    context?: Record<string, any>;
    error?: string;
    stack?: string;
}

export interface HeroConfig {
    firstImageId: string;
    secondImageId: string;
}

export interface CarouselItem {
    id: string;
    titleKey: string;
    description?: string;
    category: string;
    imageUrl: string;
    link?: string;
    buttonText?: string;
    source?: 'local' | 'blog' | 'instagram';
}

export interface AgentSession {
    userId: string;
    sessionId: string;
    agentId: string;
    history: ChatMessage[];
    updatedAt: string;
    context?: OrchestratorContext;
}
